<template>
  <validation-observer ref="form" v-slot="{ invalid }">
    <v-card flat>
      <v-row v-if="canEdit && Boolean(form.schoolId)" no-gutters>
        <v-spacer></v-spacer>
        <v-switch
          v-model="editMode"
          label="Edit Mode"
          dense
          class="mx-4"
        ></v-switch>
      </v-row>
      <v-expansion-panels v-model="panels" multiple>
        <v-expansion-panel>
          <v-expansion-panel-header>
            <span class="text-h6"
              ><v-icon left>mdi-account-outline</v-icon> Profile</span
            >
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row>
              <v-col cols="12" sm="3">
                <validation-provider rules="required" v-slot="{ errors }">
                  <v-text-field
                    v-model="form.schoolName"
                    label="School Name"
                    :readonly="!editMode"
                    :error-messages="errors"
                  ></v-text-field>
                </validation-provider>
              </v-col>
              <v-col cols="12" sm="1">
                <v-checkbox
                  v-model="form.summerCamp"
                  label="Summer Camp"
                  :readonly="!editMode"
                ></v-checkbox>
              </v-col>
              <v-col cols="12" sm="2">
                <v-autocomplete
                  v-model="form.teamId"
                  label="Team"
                  :items="teams"
                  :item-text="
                    (item) => `${item.leaderFirstName} ${item.leaderLastName}`
                  "
                  item-value="teamId"
                  :clearable="editMode"
                  :readonly="!editMode"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" sm="2">
                <v-text-field
                  v-model="form.schoolCode"
                  label="School Code"
                  :readonly="!editMode"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="2">
                <v-text-field
                  v-model="form.secondarySchoolCode"
                  label="Secondary School Code"
                  :readonly="!editMode"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="2">
                <validation-provider
                  :rules="{ minLength: 6, maxLength: 7 }"
                  v-slot="{ errors }"
                >
                  <v-text-field
                    v-model="form.dbn"
                    label="DBN"
                    :error-messages="errors"
                    :readonly="!editMode"
                  ></v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header>
            <span class="text-h6"
              ><v-icon left>mdi-map-marker-outline</v-icon> Address</span
            >
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row>
              <v-col cols="12" sm="5">
                <v-text-field
                  v-model="form.streetAddress"
                  label="Street Address"
                  :readonly="!editMode"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="form.city"
                  label="City"
                  :readonly="!editMode"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="2">
                <v-select
                  :items="usStates"
                  v-model="form.state"
                  label="State"
                  :readonly="!editMode"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="2">
                <v-text-field
                  v-model="form.zipCode"
                  label="ZipCode"
                  :readonly="!editMode"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="form.districtId"
                  label="District"
                  :readonly="!editMode"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="form.secondaryDistrictId"
                  label="Secondary District ID"
                  :readonly="!editMode"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="form.schoolBoro"
                  label="School Boro"
                  :readonly="!editMode"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="form.secondarySchoolBoro"
                  label="Secondary School Boro"
                  :readonly="!editMode"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header>
            <span class="text-h6"
              ><v-icon left>mdi-phone-outline</v-icon> Contact</span
            >
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.contact"
                  label="Contact"
                  :readonly="!editMode"
                ></v-text-field
              ></v-col>
              <v-col cols="12" sm="6">
                <validation-provider
                  rules="email"
                  immediate
                  v-slot="{ errors }"
                >
                  <v-text-field
                    v-model="form.emailAddress"
                    label="Email Address"
                    :error-messages="errors"
                    :readonly="!editMode"
                  ></v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="form.phone"
                  label="Phone"
                  v-mask="maskPhone"
                  :readonly="!editMode"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="2">
                <v-text-field
                  v-model="form.phoneExtension"
                  label="Extension"
                  :readonly="!editMode"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="form.secondaryPhone"
                  label="Secondary Phone"
                  v-mask="maskPhone"
                  :readonly="!editMode"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="2">
                <v-text-field
                  v-model="form.secondaryPhoneExtension"
                  label="Secondary Phone Extension"
                  :readonly="!editMode"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="2">
                <v-text-field
                  v-model="form.fax"
                  label="Fax"
                  :readonly="!editMode"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header>
            <span class="text-h6"
              ><v-icon left>mdi-comment-text-multiple-outline</v-icon>
              Notes</span
            >
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row>
              <v-col cols="12" sm="12">
                <v-textarea
                  v-model="form.notes"
                  label="Notes"
                  rows="1"
                  autoGrow
                  :readonly="!editMode"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-card-actions v-if="canEdit">
        <v-spacer></v-spacer>
        <v-btn @click="cancel" text>Cancel</v-btn>
        <v-btn
          @click="save"
          :disabled="loading || invalid || !editMode"
          color="primary"
          >Save</v-btn
        >
      </v-card-actions>
    </v-card>
  </validation-observer>
</template>

<script>
import School from "../../objs/School";
import { clearPhoneNumberFormat } from "../../utils/format-utils";
export default {
  props: ["schoolId", "clientId"],
  data() {
    return {
      form: new School(),
      editMode: !Boolean(this.schoolId),
      maskPhone: "(###) ###-####",
      panels: [0, 1, 2, 3],
      loading: false,
    };
  },
  computed: {
    canEdit() {
      return this.$store.getters["securityModule/isSEITOrRSAdmin"];
    },
    teams() {
      return this.$store.state.optionsModule.teams;
    },
    usStates() {
      return this.$store.state.optionsModule.usStates;
    },
  },
  mounted() {
    if (this.schoolId) {
      this.form = {
        ...this.$store.getters["optionsModule/getSchoolById"](this.schoolId),
      };
    }
  },
  methods: {
    cancel() {
      if (this.clientId != null) {
        this.$router.push({
          name: "ClientDetails",
          params: {
            clientId: this.clientId,
          },
        });
      } else {
        this.$router.push({ name: "SchoolLookup" });
      }
    },
    async save() {
      this.loading = true;
      this.$store.commit("uxModule/setShowLoader", true);
      //format form fields
      this.form.phone = clearPhoneNumberFormat(this.form.phone);
      this.form.secondaryPhone = clearPhoneNumberFormat(
        this.form.secondaryPhone
      );
      let response = { schoolId: null, message: "" };
      if (this.form.schoolId) {
        response = await this.$store.dispatch(
          "optionsModule/updateSchool",
          this.form
        );
      } else {
        response = await this.$store.dispatch(
          "optionsModule/createSchool",
          this.form
        );
        // if they created a school from a client's school registration page -> go back to the client's page
        if (this.clientId != null) {
          this.$router.push({
            name: "ClientDetails",
            params: {
              clientId: this.clientId,
            },
          });
        }
        // else -> load the School
        else {
          this.$router.push({
            name: "SchoolDetails",
            params: {
              schoolId: response.schoolId,
            },
          });
        }
      }
      if (response.message) {
        this.$store.commit("uxModule/setSnackbarMsg", response.message);
      } else {
        this.$store.commit(
          "uxModule/setSnackbarMsg",
          "School saved successfully"
        );
      }
      this.loading = false;
      this.editMode = false;
      this.$store.commit("uxModule/setShowLoader", false);
      this.$store.commit("uxModule/setShowSnackbar", true);
    },
  },
};
</script>